import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import Loader from '../utils/Loader.js'
import gsap from 'gsap'
import buildShaders from './ProjectShader.js'

export default class Projects{
  constructor(projectList){
    this.projectList = projectList

    this.scene
    this.camera
    this.updateInterface = null
    this.modelList = []
    this.uiResizeFunction
    this.Loader = new Loader()

    this.nav = {
      projectOffset: 3,
      isScreenHold: false,
      anim: null,
      lastY: null,
      startDragID: null,
      speed: 0,
      maxY: 0,
      lastID: 0
    }
    this.lerp = 0.05
    this.cameraDestination = 0
    this.uniforms = {
      uTime: { value: 0 },
      uCameraPosition: { value: 0 }
    }

    this.directionalLight

    this.positionTimeout = null
    this.cameraAnimation = null

    this.setupScene()
    this.setCamera()
    this.setLights()

    this.shader = buildShaders(this.uniforms)

    this.populateProjects()
    this.updateProjectsPositions()
  }

  // ===== //
  // Setup //
  // ===== //
  setupScene(){
    this.scene = new THREE.Scene()
  }

  setCamera(){
    this.camera = new THREE.PerspectiveCamera(75, 0, 0.1, 10)
    this.camera.lookAt(new THREE.Vector3(0, 0,0))
    this.scene.add(this.camera)
  }

  setLights(){
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5)
    this.scene.add(ambientLight)

    this.directionalLight = new THREE.DirectionalLight(0xffffff, 2)
    this.directionalLight.position.set( -2, 2, 0 )

    this.directionalLight.castShadow = true
    this.directionalLight.shadow.mapSize.set(1024, 1024)

    this.directionalLight.shadow.camera.near = 1
    this.directionalLight.shadow.camera.far = 5

    this.directionalLight.shadow.camera.left = -0.8
    this.directionalLight.shadow.camera.right = 0.8
    this.directionalLight.shadow.camera.top = 2
    this.directionalLight.shadow.camera.bottom = -2

    this.directionalLight.shadow.normalBias = 0.002
    this.directionalLight.shadow.radius = 1
    this.directionalLight.shadow.camera.updateProjectionMatrix()

    this.scene.add(this.directionalLight, this.directionalLight.target)
  }

  populateProjects(){
    // Geometry
    const gltfLoader = new GLTFLoader()

    let i = 0
    gltfLoader.load('./assets/models/projects.glb', gltf => {
      // Model
      const modeList = gltf.scene
      for(let project of this.projectList){
        const mesh = modeList.children.find(child => child.name === project.model )

        mesh.castShadow = true
        mesh.receiveShadow = true

        mesh.material = this.shader.material
        mesh.customDepthMaterial = this.shader.depth

        mesh.name = i
        i++

        this.modelList.push(mesh)
        this.scene.add(mesh)
      }
      this.Loader.close()
      this.updateProjectsPositions()
    })
  }

  updateProjectsPositions(){
    for(let model of this.modelList){
      const length = model.geometry.attributes.position.count
      const sizes = new Float32Array(length)
      const modelPosition = -Number(model.name) * this.nav.projectOffset

      for (let i = 0; i < length; i++){
        sizes[i] = modelPosition
      }

      model.geometry.setAttribute('aPosY', new THREE.BufferAttribute(sizes, 1))
      model.position.y = modelPosition
    }
  }

  start(event){
    this.nav.lastY = event.clientY
    this.nav.isScreenHold = true
    this.nav.startDragID = this.getId()

    this.clearTimers()
  }

  stop(isFlat){
    if(this.nav.isScreenHold || isFlat){
      this.nav.isScreenHold = false

      this.clearTimers()
      this.positionTimeout = setTimeout(() => {
        const valueHolder = {
          y: this.cameraDestination
        }
        const local = this

        this.cameraAnimation = gsap.to(valueHolder, {
          y: -this.getId() * this.nav.projectOffset,
          duration: 1,
          onUpdate:function(){
            local.cameraDestination = valueHolder.y
          }
        })
      }, 1000)
    }
  }

  drag(y, isFlat){
    if (this.nav.isScreenHold || isFlat){

      const variation = isFlat ? y : (y - this.nav.lastY)
      let newY = this.cameraDestination + (variation * this.nav.speed)

      this.nav.lastY = y

      if (newY < -this.nav.maxY - 0.3){
        newY = -this.nav.maxY - 0.3
      }
      else if (newY > 0.3){
        newY = 0.3
      }

      this.cameraDestination = newY
    }
  }

  clearTimers(){
    if(this.positionTimeout){
      clearTimeout(this.positionTimeout)
    }
    if(this.cameraAnimation){
      this.cameraAnimation.kill()
    }
  }

  getId(){
    return Math.round(Math.abs(this.cameraDestination) / this.nav.projectOffset)
  }

  animateCamera(){
    // Calculate Scroll
    const difference = this.cameraDestination - this.camera.position.y
    const movement = difference * this.lerp

    this.camera.position.y += movement

    this.uniforms.uTime.value = performance.now()
    this.uniforms.uCameraPosition.value = this.camera.position.y

    // Update Light
    this.directionalLight.position.set(- 2, this.camera.position.y + 2, 0)
    this.directionalLight.target.position.set(0, this.camera.position.y, 0)
  }

  resize(sizes){
    this.nav.speed = sizes.width < sizes.height ? 0.012 : 0.008
    this.camera.position.z = sizes.width < sizes.height ? 2 : 1.5

    // Set Camera max X
    this.nav.maxY = (this.projectList.length - 1) * this.nav.projectOffset

    // Update camera
    this.camera.aspect = sizes.width / sizes.height
    this.camera.updateProjectionMatrix()

    // Resize ui
    this.uiResizeFunction(sizes)
  }
}
